import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Sidebar } from '../../shared/components/sidebar/sidebar';
import { Navbar } from '../../shared/components/navbar/navbar';
import { Toast } from '../../shared/services/toast';
import { Toast as ToastComponent } from '../../shared/components/toast/toast';
import { Batch } from '../../core/services/batch';

// Total per upload, matching the backend's UploadBatch MaxUploadBatchBytes -- kept in
// sync manually since there's no shared config endpoint for it.
const MAX_UPLOAD_BYTES = 1024 * 1024 * 1024;

@Component({
  selector: 'app-upload-documents',
  standalone: true,
  imports: [CommonModule, FormsModule, Sidebar, Navbar, ToastComponent],
  templateUrl: './upload-documents.html',
  styleUrl: './upload-documents.css'
})
export class UploadDocumentsPage {
  constructor(private batchService: Batch, private toast: Toast, private router: Router) {}

  name = signal('');
  description = signal('');
  selectedFiles = signal<File[]>([]);
  isUploading = signal(false);

  goBack() {
    this.router.navigate(['/data-sources']);
  }

  totalBytes(): number {
    return this.selectedFiles().reduce((sum, f) => sum + f.size, 0);
  }

  isOverLimit(): boolean {
    return this.totalBytes() > MAX_UPLOAD_BYTES;
  }

  /** Stages picked files instead of uploading immediately, so the user can review/add more before proceeding -- same pattern as Pre Req's business documents tab. */
  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (files.length === 0) return;

    this.selectedFiles.update(existing => {
      const isDuplicate = (f: File) => existing.some(e => e.name === f.name && e.size === f.size);
      return [...existing, ...files.filter(f => !isDuplicate(f))];
    });
  }

  removeSelectedFile(index: number) {
    this.selectedFiles.update(files => files.filter((_, i) => i !== index));
  }

  clearSelectedFiles() {
    this.selectedFiles.set([]);
  }

  canSubmit(): boolean {
    return this.name().trim().length > 0 && this.selectedFiles().length > 0 && !this.isOverLimit() && !this.isUploading();
  }

  submit() {
    if (!this.canSubmit()) return;

    this.isUploading.set(true);
    this.batchService.uploadBatch(this.name().trim(), this.description().trim() || undefined, this.selectedFiles()).subscribe({
      next: () => {
        this.isUploading.set(false);
        this.toast.show(`Batch "${this.name().trim()}" created. Start ingestion from Manage Batches when you're ready.`, 'success');
        this.router.navigate(['/manage-batches']);
      },
      error: (err) => {
        this.isUploading.set(false);
        this.toast.show(err?.error?.message || 'Failed to upload documents.', 'error');
      }
    });
  }

  formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
